import { useEffect, useRef } from "react";

import { Button as ButtonV2 } from "../Button";
import { useIsMobile } from "../hooks/useIsMobile";
import { InputV2 } from "../InputV2";
import { ResponsiveModal } from "../ResponsiveModal";

export const RENAME_CHAR_LIMIT = 60;

export type RenameLinkModalTranslations = {
  title: string;
  save: string;
  cancel: string;
};

export type RenameLinkModalAnalytics = {
  trackRenameSaveEvent?: (name: string) => void;
  trackRenameCancelEvent?: () => void;
};

type RenameLinkModalProps = {
  name: string;
  error: string;
  isSaving: boolean;
  isSaveDisabled: boolean;
  translations: RenameLinkModalTranslations;
  onClose: () => void;
  onNameChange: (value: string) => void;
  onSave: () => void;
  analytics?: RenameLinkModalAnalytics;
};

export const RenameLinkModal = ({
  name,
  error,
  isSaving,
  isSaveDisabled,
  translations,
  onClose,
  onNameChange,
  onSave,
  analytics,
}: RenameLinkModalProps) => {
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timeout);
  }, []);

  const handleCancel = () => {
    analytics?.trackRenameCancelEvent?.();
    onClose();
  };

  const handleSave = () => {
    analytics?.trackRenameSaveEvent?.(name.trim());
    onSave();
  };

  const footer = (
    <div className="flex w-full flex-row gap-3 lg:justify-end lg:gap-4">
      <ButtonV2
        variant="outline"
        className="flex-1 lg:flex-none"
        onClick={handleCancel}
        disabled={isSaving}
        data-testid="referral_link_rename_modal_cancel_button"
      >
        {translations.cancel}
      </ButtonV2>
      <ButtonV2
        variant="brand"
        className="flex-1 lg:flex-none"
        onClick={handleSave}
        disabled={isSaveDisabled}
        isLoading={isSaving}
        data-testid="referral_link_rename_modal_save_button"
      >
        {translations.save}
      </ButtonV2>
    </div>
  );

  return (
    <ResponsiveModal
      isOpen
      onClose={handleCancel}
      isMobile={isMobile}
      title={translations.title}
      footer={footer}
      maxWidth={480}
      contentOverflow="visible"
    >
      <InputV2
        ref={inputRef}
        name="rename-link"
        value={name}
        characterLimit={RENAME_CHAR_LIMIT}
        maxLength={RENAME_CHAR_LIMIT}
        onChange={onNameChange}
        error={error}
        showError={!!error}
        data-testid="referral_link_rename_modal_name_input"
      />
    </ResponsiveModal>
  );
};
